import controller from "@ctrl/controller";
import type {
  Block,
  Controller,
  Document,
  TaskRead,
} from "@ctrl/controller-rpc";

import { CALLOUT_COLORS } from "@/components/editor/prosemirror/callout-types";
import { runQuery, setGraphProperty } from "@/components/graph/component";
import { toGraphDocument } from "@/components/graph/properties/document-serialization";
import { Menu } from "@/composables/useMenu";
import { UseProfile } from "@/composables/useProfile";

import { createGuidePage } from "./guidePage";

const { makeBlockId } = controller;

export type OnboardingUseCase =
  | {
      type: "individual";
    }
  | {
      type: "team";
      organizationId: string;
    };

/** Short helpers for tutorial Notes `Document` values. */
const noteDoc = {
  make: (...blocks: Block[]): Document => ({ blocks }),
  embed: (url: string): Block => ({
    id: makeBlockId(),
    type: "embed",
    content: url,
  }),
  /** Single embed block (typical tutorial video). */
  video: (url: string): Document => noteDoc.make(noteDoc.embed(url)),
  /** Ordered list steps (each item is one numbered bullet block). */
  orderedList: (...items: string[]): Document => ({
    blocks: items.map((content) => ({
      id: makeBlockId(),
      type: "bullet" as const,
      list_type: "ordered" as const,
      content,
    })),
  }),
  callout: (content: string): Block => {
    const { r, g, b } = CALLOUT_COLORS.info;
    return {
      id: makeBlockId(),
      type: "callout",
      content,
      background_color: [r, g, b],
      icon: { kind: "emoji", emoji: "ℹ️" },
    };
  },
};

const supportBlocks = (): Block[] => [
  {
    id: makeBlockId(),
    type: "heading",
    level: 1,
    content: "Support",
  },
  {
    id: makeBlockId(),
    type: "paragraph",
    content: "If you need any help, please refer to the following resources:",
  },
  {
    id: makeBlockId(),
    type: "bullet",
    list_type: "unordered",
    content: "📚 [Help center](https://routine.co/help)",
  },
  {
    id: makeBlockId(),
    type: "bullet",
    list_type: "unordered",
    content: "💬 [Chat](https://routine.co/chat)",
  },
  {
    id: makeBlockId(),
    type: "bullet",
    list_type: "unordered",
    content: "📋 [Community forum](https://routine.co/community)",
  },
  {
    id: makeBlockId(),
    type: "bullet",
    list_type: "unordered",
    content: "📺 [YouTube channel](https://www.youtube.com/@routinehq)",
  },
  {
    id: makeBlockId(),
    type: "bullet",
    list_type: "unordered",
    content: "🐞 [Feedback portal](https://routine.co/feedback)",
  },
];

type TutorialLevel =
  | "1. Fundamentals"
  | "2. Advanced"
  | "3. AI"
  | "4. Teamwork";

/** Tutorial row seed; `rowId` is set after the row is created in `useOnboarding`. */
export type Tutorial = {
  title: string;
  level: TutorialLevel;
  content: Document;
  rowId?: string;
};

/** Row id for a tutorial after onboarding has created rows (`rowId` is set on each entry). */
export function getTutorialRowIdByTitle(
  tutorials: readonly Tutorial[],
  title: string
): string | undefined {
  return tutorials.find((t) => t.title === title)?.rowId;
}

export async function useOnboarding(
  controller: Controller,
  profile: UseProfile,
  menu: Menu,
  onboardingUseCase: OnboardingUseCase
) {
  const personalOrganizationId = profile.user.personal.organizationId;
  const personalWorkspaceId = profile.user.personal.workspaceId;

  // Create the tutorials database
  const titleColumnId = await controller.graph.database.generateColumnId();
  const levelColumnId = await controller.graph.database.generateColumnId();
  const watchedColumnId = await controller.graph.database.generateColumnId();
  const notesColumnId = await controller.graph.database.generateColumnId();

  const tutorialsDatabaseResult = await controller.graph.database.add(
    personalWorkspaceId,
    "Tutorial",
    "tutorials",
    { emoji: "🎓" },
    {
      type: "record",
      fields: {
        [titleColumnId]: {
          name: "Title",
          value: { type: "string" },
        },
        [levelColumnId]: {
          name: "Level",
          // GraphCustomType has no `select`; fixed options are a variant of string_const (see SelectDefinition.vue).
          value: {
            type: "variant",
            cases: [
              { type: "string_const", value: "1. Fundamentals" },
              { type: "string_const", value: "2. Advanced" },
              { type: "string_const", value: "3. AI" },
              { type: "string_const", value: "4. Teamwork" },
            ],
          },
        },
        [watchedColumnId]: {
          name: "Watched",
          value: { type: "bool" },
        },
        [notesColumnId]: {
          name: "Notes",
          value: { type: "note" },
        },
      },
    },
    "Tutorials to learn to get started with Routine."
  );

  // Create the tutorials.
  if (!("value" in tutorialsDatabaseResult)) return;

  const tutorialsTable = (
    await controller.graph.database.get(
      personalWorkspaceId,
      tutorialsDatabaseResult.value
    )
  ).release();
  if (!tutorialsTable) return;
  const tutorialsTableId = tutorialsTable.id;

  const tutorials: Tutorial[] = [
    {
      title: "101: Capture everything",
      level: "1. Fundamentals",
      content: noteDoc.video("https://youtu.be/8sdO4E_rbys"),
    },
    {
      title: "102: Focus on what matters",
      level: "1. Fundamentals",
      content: noteDoc.video("https://youtu.be/xgY5XiKgKMo"),
    },
    {
      title: "103: Manage your time",
      level: "1. Fundamentals",
      content: noteDoc.video("https://youtu.be/fbNpxHOp1tc"),
    },
    {
      title: "104: Customize the app",
      level: "1. Fundamentals",
      content: noteDoc.video("https://youtu.be/z1wrEdlcraM"),
    },
    {
      title: "105: Take notes on anything",
      level: "1. Fundamentals",
      content: noteDoc.video("https://youtu.be/HKIyr3hngfE"),
    },
    {
      title: "106: Organize with labels & priorities",
      level: "1. Fundamentals",
      content: noteDoc.video("https://youtu.be/_slaPXfHRYA"),
    },
    {
      title: "107: Track your time",
      level: "1. Fundamentals",
      content: noteDoc.video("https://youtu.be/kOu_A2p2euk"),
    },
    {
      title: "201: Define your own data model",
      level: "2. Advanced",
      content: noteDoc.video("https://youtu.be/_hmpknZ0OP8"),
    },
    {
      title: "202: Synchronize data from your apps",
      level: "2. Advanced",
      content: noteDoc.video("https://youtu.be/r7Pz7-G_Yhw"),
    },
    {
      title: "203: Install extensions",
      level: "2. Advanced",
      content: noteDoc.orderedList(
        "Visit the [Marketplace](https://routine.co/marketplace)",
        "Explore to find an extension that can supercharge your workflow",
        "Click Install to add the extension in your Routine account"
      ),
    },
    {
      title: "204: Compose your own screens",
      level: "2. Advanced",
      content: noteDoc.make(
        noteDoc.callout(
          "This functionality is not yet available to the general public."
        )
      ),
    },
    {
      title: "301: Automatically record meetings",
      level: "3. AI",
      content: noteDoc.video("https://youtu.be/wrbh2ZOibeE"),
    },
    {
      title: "302: Delegate to your assistant",
      level: "3. AI",
      content: noteDoc.video("https://youtu.be/dTDMJXQHRv8"),
    },
    {
      title: "303: Add agents to your workforce",
      level: "3. AI",
      content: noteDoc.video("https://youtu.be/f-m5kgw3L3U"),
    },
    {
      title: "304: Automate your workflows",
      level: "3. AI",
      content: noteDoc.video("https://youtu.be/D3Vp5B7dqBY"),
    },
    {
      title: "305: Talk to your voice assistant",
      level: "3. AI",
      content: noteDoc.video("https://youtu.be/1sjWknedhiU"),
    },
    {
      title: "401: Create and organize your team",
      level: "4. Teamwork",
      content: noteDoc.video("https://youtu.be/P0LHEohkNpM"),
    },
    {
      title: "402: Install extensions",
      level: "4. Teamwork",
      content: noteDoc.make({
        id: makeBlockId(),
        type: "paragraph",
        content:
          "Browse the [Routine marketplace](https://routine.co/marketplace) to discover extensions created by the community that you can easily install to further enhance your workflows.",
      }),
    },
    {
      title: "403: Adapt your data model to how your team works",
      level: "4. Teamwork",
      content: noteDoc.video("https://youtu.be/_hmpknZ0OP8"),
    },
    {
      title: "404: Collaborate & assign work",
      level: "4. Teamwork",
      content: noteDoc.video("https://youtu.be/4Zrg9Eg-sQk"),
    },
  ];

  for (const tutorial of tutorials) {
    const rowId = await runQuery(
      personalWorkspaceId,
      "databases.[dbId].rows add { Title: title, Level: level, Watched: watched }",
      {
        dbId: tutorialsTableId,
        title: tutorial.title,
        level: tutorial.level,
        watched: false,
      },
      controller
    );

    if (!rowId) {
      continue;
    }

    tutorial.rowId = rowId;

    if (tutorial.content.blocks.length > 0) {
      setGraphProperty(
        toGraphDocument(tutorial.content),
        "Notes",
        rowId,
        tutorialsTable,
        controller,
        undefined
      );
    }
  }

  // Create the guide page
  const guideId = await controller.page.create("🎈 Guide", null, "app");

  // Pin the guide page to the menu
  await menu.pinObject(guideId, "page");

  // Populate today task
  await controller.task.create(
    {
      title: "🖥️ Download the Routine apps",
      scheduled: new Date(),
      notes: {
        blocks: [
          {
            id: makeBlockId(),
            type: "blockquote",
            content:
              "Users who install Routine on desktop and mobile are 7x more likely to complete their work in time!",
          },
          {
            id: makeBlockId(),
            type: "paragraph",
            content:
              "The Routine apps for desktop (macOS/Windows/Linux) and mobile (iOS/Android):",
          },
          {
            id: makeBlockId(),
            type: "bullet",
            list_type: "unordered",
            content:
              "🌐 **Work offline** so you never depend on your Internet connection",
          },
          {
            id: makeBlockId(),
            type: "bullet",
            list_type: "unordered",
            content: "⚡ Are up to **70% faster** than the Web app",
          },
          {
            id: makeBlockId(),
            type: "bullet",
            list_type: "unordered",
            content:
              "🚀 Come equipped with **unique features** that are not available in Web:",
          },
          {
            id: makeBlockId(),
            type: "bullet",
            list_type: "unordered",
            depth: 2,
            content: "[Quick capture](https://routine.co/features/dashboard)",
          },
          {
            id: makeBlockId(),
            type: "bullet",
            list_type: "unordered",
            depth: 2,
            content:
              "[Menu bar widget](https://routine.co/features/menu-bar-widget)",
          },
          {
            id: makeBlockId(),
            type: "bullet",
            list_type: "unordered",
            depth: 2,
            content:
              "[Contextual capture](https://routine.co/features/contextual-capture)",
          },
          {
            id: makeBlockId(),
            type: "bullet",
            list_type: "unordered",
            depth: 2,
            content: "[Offline mode](https://routine.co/features/offline)",
          },
          {
            id: makeBlockId(),
            type: "bullet",
            list_type: "unordered",
            depth: 2,
            content:
              "[AI voice commands](https://routine.co/features/ai-voice-commands)",
          },
          {
            id: makeBlockId(),
            type: "bullet",
            list_type: "unordered",
            depth: 2,
            content:
              "[AI meeting recording](https://routine.co/features/meeting-notes)",
          },
          {
            id: makeBlockId(),
            type: "bullet",
            list_type: "unordered",
            depth: 2,
            content:
              "[Time tracking](https://routine.co/features/time-tracking)",
          },
          {
            id: makeBlockId(),
            type: "bullet",
            list_type: "unordered",
            depth: 2,
            content: "and more...",
          },
          {
            id: makeBlockId(),
            type: "paragraph",
            content: "",
          },
          {
            id: makeBlockId(),
            type: "paragraph",
            content: "**→** [Download now!](https://routine.co/download)",
          },
        ],
      },
    },
    { source: "other" }
  );

  // Create a task depending on the use case
  switch (onboardingUseCase.type) {
    case "individual": {
      const individualWelcomeTask = {
        title: "🎊 Double click me to get started",
        scheduled: new Date(),
        notes: {
          blocks: [
            {
              id: makeBlockId(),
              type: "heading",
              level: 1,
              content: "Welcome",
            },
            {
              id: makeBlockId(),
              type: "paragraph",
              content: "Get started by performing the following steps:",
            },
            {
              id: makeBlockId(),
              type: "check",
              checked: false,
              content: `[Connect](/settings/organizations/${personalOrganizationId}/integrations?nature=calendars) your calendar`,
            },
            {
              id: makeBlockId(),
              type: "check",
              checked: false,
              content: "Create a task",
            },
            {
              id: makeBlockId(),
              type: "check",
              checked: false,
              content: "Drag & drop a task onto your calendar",
            },
            {
              id: makeBlockId(),
              type: "check",
              checked: false,
              content: "Open the task and add notes",
            },
            {
              id: makeBlockId(),
              type: "heading",
              level: 1,
              content: "Guide",
            },
            {
              id: makeBlockId(),
              type: "paragraph",
              content: `Go further by browsing through Routine's **#${guideId}**.`,
            },
            ...supportBlocks(),
          ],
        },
      } satisfies TaskRead;
      await controller.task.create(individualWelcomeTask, {
        source: "other",
      });
      break;
    }
    case "team": {
      const organizationId = onboardingUseCase.organizationId;
      const rowId401 = getTutorialRowIdByTitle(
        tutorials,
        "401: Create and organize your team"
      );
      const path401 = `/objects/${personalWorkspaceId}/${tutorialsTableId}/${rowId401}`;
      const rowId201 = getTutorialRowIdByTitle(
        tutorials,
        "201: Define your own data model"
      );
      const path201 = `/objects/${personalWorkspaceId}/${tutorialsTableId}/${rowId201}`;
      const rowId202 = getTutorialRowIdByTitle(
        tutorials,
        "202: Synchronize data from your apps"
      );
      const path202 = `/objects/${personalWorkspaceId}/${tutorialsTableId}/${rowId202}`;
      const pathOrganizationPeople = `/settings/organizations/${organizationId}/people`;
      const teamWelcomeTask = {
        title: "👥 Set up your team",
        scheduled: new Date(),
        notes: {
          blocks: [
            {
              id: makeBlockId(),
              type: "heading",
              level: 1,
              content: "Welcome",
            },
            {
              id: makeBlockId(),
              type: "paragraph",
              content: `Get started by setting up your team's organization:`,
            },
            {
              id: makeBlockId(),
              type: "check",
              checked: false,
              content: `[Invite people](${pathOrganizationPeople}) to join your organization`,
            },
            {
              id: makeBlockId(),
              type: "check",
              checked: false,
              content: `[Create workspaces](${path401}) to control access`,
            },
            {
              id: makeBlockId(),
              type: "check",
              checked: false,
              content: `Define your [data model](${path201})`,
            },
            {
              id: makeBlockId(),
              type: "check",
              checked: false,
              content: `Connect your [integrations](${path202}) to sync your data`,
            },
            {
              id: makeBlockId(),
              type: "heading",
              level: 1,
              content: "Guide",
            },
            {
              id: makeBlockId(),
              type: "paragraph",
              content: `Go further by browsing through Routine's **#${guideId}**.`,
            },
            ...supportBlocks(),
          ],
        },
      } satisfies TaskRead;
      await controller.task.create(teamWelcomeTask, {
        source: "other",
      });
      break;
    }
  }

  await createGuidePage(guideId, controller);
}
